import { readScopedStorage, writeScopedStorage } from './userPreferenceScope';

export type ColorTheme =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'rose'
  | 'amber'
  | 'indigo'
  | 'teal'
  | 'orange';

export const THEME_STORAGE_KEY = 'ef_app_color_theme_v1';
export const THEME_CHANGED_EVENT = 'ef-theme-changed';

type Shade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

export type ThemePalette = {
  id: ColorTheme;
  labelKey: string;
  swatch: string;
  accent: string;
  accentStrong: string;
  secondary: string;
  shades: Record<Shade, string>;
  secondaryShades: Record<Shade, string>;
};

const SHADES: Shade[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

function palette(
  id: ColorTheme,
  labelKey: string,
  swatch: string,
  accent: string,
  accentStrong: string,
  secondary: string,
  shades: Record<Shade, string>,
  secondaryShades: Record<Shade, string>
): ThemePalette {
  return { id, labelKey, swatch, accent, accentStrong, secondary, shades, secondaryShades };
}

export const THEME_PALETTES: ThemePalette[] = [
  palette(
    'emerald',
    'theme.emerald',
    '#059669',
    '#059669',
    '#047857',
    '#0d9488',
    {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
      950: '#022c22',
    },
    {
      50: '#f0fdfa',
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6',
      600: '#0d9488',
      700: '#0f766e',
      800: '#115e59',
      900: '#134e4a',
      950: '#042f2e',
    }
  ),
  palette(
    'blue',
    'theme.blue',
    '#2563eb',
    '#2563eb',
    '#1d4ed8',
    '#0284c7',
    {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554',
    },
    {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
      950: '#082f49',
    }
  ),
  palette(
    'violet',
    'theme.violet',
    '#7c3aed',
    '#7c3aed',
    '#6d28d9',
    '#9333ea',
    {
      50: '#f5f3ff',
      100: '#ede9fe',
      200: '#ddd6fe',
      300: '#c4b5fd',
      400: '#a78bfa',
      500: '#8b5cf6',
      600: '#7c3aed',
      700: '#6d28d9',
      800: '#5b21b6',
      900: '#4c1d95',
      950: '#2e1065',
    },
    {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7e22ce',
      800: '#6b21a8',
      900: '#581c87',
      950: '#3b0764',
    }
  ),
  palette(
    'rose',
    'theme.rose',
    '#e11d48',
    '#e11d48',
    '#be123c',
    '#db2777',
    {
      50: '#fff1f2',
      100: '#ffe4e6',
      200: '#fecdd3',
      300: '#fda4af',
      400: '#fb7185',
      500: '#f43f5e',
      600: '#e11d48',
      700: '#be123c',
      800: '#9f1239',
      900: '#881337',
      950: '#4c0519',
    },
    {
      50: '#fdf2f8',
      100: '#fce7f3',
      200: '#fbcfe8',
      300: '#f9a8d4',
      400: '#f472b6',
      500: '#ec4899',
      600: '#db2777',
      700: '#be185d',
      800: '#9d174d',
      900: '#831843',
      950: '#500724',
    }
  ),
  palette(
    'amber',
    'theme.amber',
    '#d97706',
    '#d97706',
    '#b45309',
    '#ea580c',
    {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
      950: '#451a03',
    },
    {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
      950: '#431407',
    }
  ),
  palette(
    'indigo',
    'theme.indigo',
    '#4f46e5',
    '#4f46e5',
    '#4338ca',
    '#6366f1',
    {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
      950: '#1e1b4b',
    },
    {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
      950: '#1e1b4b',
    }
  ),
  palette(
    'teal',
    'theme.teal',
    '#0d9488',
    '#0d9488',
    '#0f766e',
    '#0891b2',
    {
      50: '#f0fdfa',
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6',
      600: '#0d9488',
      700: '#0f766e',
      800: '#115e59',
      900: '#134e4a',
      950: '#042f2e',
    },
    {
      50: '#ecfeff',
      100: '#cffafe',
      200: '#a5f3fc',
      300: '#67e8f9',
      400: '#22d3ee',
      500: '#06b6d4',
      600: '#0891b2',
      700: '#0e7490',
      800: '#155e75',
      900: '#164e63',
      950: '#083344',
    }
  ),
  palette(
    'orange',
    'theme.orange',
    '#ea580c',
    '#ea580c',
    '#c2410c',
    '#dc2626',
    {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
      950: '#431407',
    },
    {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
      950: '#450a0a',
    }
  ),
];

const THEME_STYLE_ID = 'ef-theme-overrides';

export function themePalette(theme: ColorTheme): ThemePalette {
  return THEME_PALETTES.find((p) => p.id === theme) ?? THEME_PALETTES[0];
}

/** Hex colors for PDF / Excel branding (follows current UI color theme). */
export type PdfThemeColors = {
  brand: string;
  brandStrong: string;
  headerBorder: string;
  thBgFrom: string;
  thBgTo: string;
  thText: string;
  thBorder: string;
  excelHeader: string;
};

export function readColorTheme(): ColorTheme {
  if (typeof localStorage === 'undefined') return 'emerald';
  const raw = readScopedStorage(THEME_STORAGE_KEY);
  if (THEME_PALETTES.some((p) => p.id === raw)) return raw as ColorTheme;
  return 'emerald';
}

export function getPdfThemeColors(theme?: ColorTheme): PdfThemeColors {
  const p = themePalette(theme ?? readColorTheme());
  return {
    brand: p.secondaryShades[700] || p.shades[700],
    brandStrong: p.shades[700],
    headerBorder: p.secondaryShades[700] || p.shades[700],
    thBgFrom: p.shades[50],
    thBgTo: p.shades[100],
    thText: p.shades[800],
    thBorder: p.secondaryShades[700] || p.shades[700],
    excelHeader: p.shades[700],
  };
}

export function writeColorTheme(theme: ColorTheme): void {
  if (typeof localStorage === 'undefined') return;
  writeScopedStorage(THEME_STORAGE_KEY, theme);
  window.dispatchEvent(new Event(THEME_CHANGED_EVENT));
}

function buildOverrideCss(theme: ColorTheme): string {
  if (theme === 'emerald') return '';
  const p = themePalette(theme);
  const selectors: string[] = [];

  for (const shade of SHADES) {
    const primary = p.shades[shade];
    const secondary = p.secondaryShades[shade];
    selectors.push(`[data-theme="${theme}"] .bg-emerald-${shade}{background-color:${primary}!important}`);
    selectors.push(`[data-theme="${theme}"] .text-emerald-${shade}{color:${primary}!important}`);
    selectors.push(`[data-theme="${theme}"] .border-emerald-${shade}{border-color:${primary}!important}`);
    selectors.push(`[data-theme="${theme}"] .ring-emerald-${shade}{--tw-ring-color:${primary}!important}`);
    selectors.push(`[data-theme="${theme}"] .divide-emerald-${shade}>:not([hidden])~:not([hidden]){border-color:${primary}!important}`);
    selectors.push(`[data-theme="${theme}"] .from-emerald-${shade}{--tw-gradient-from:${primary} var(--tw-gradient-from-position)!important}`);
    selectors.push(`[data-theme="${theme}"] .to-emerald-${shade}{--tw-gradient-to:${primary} var(--tw-gradient-to-position)!important}`);
    selectors.push(`[data-theme="${theme}"] .via-emerald-${shade}{--tw-gradient-via:${primary} var(--tw-gradient-via-position)!important}`);

    selectors.push(`[data-theme="${theme}"] .bg-teal-${shade}{background-color:${secondary}!important}`);
    selectors.push(`[data-theme="${theme}"] .text-teal-${shade}{color:${secondary}!important}`);
    selectors.push(`[data-theme="${theme}"] .border-teal-${shade}{border-color:${secondary}!important}`);
    selectors.push(`[data-theme="${theme}"] .to-teal-${shade}{--tw-gradient-to:${secondary} var(--tw-gradient-to-position)!important}`);
    selectors.push(`[data-theme="${theme}"] .from-teal-${shade}{--tw-gradient-from:${secondary} var(--tw-gradient-from-position)!important}`);

    selectors.push(`[data-theme="${theme}"] .hover\\:bg-emerald-${shade}:hover{background-color:${primary}!important}`);
    selectors.push(`[data-theme="${theme}"] .hover\\:text-emerald-${shade}:hover{color:${primary}!important}`);
    selectors.push(`[data-theme="${theme}"] .hover\\:border-emerald-${shade}:hover{border-color:${primary}!important}`);
    selectors.push(`[data-theme="${theme}"] .focus\\:ring-emerald-${shade}:focus{--tw-ring-color:${primary}!important}`);
    selectors.push(`[data-theme="${theme}"] .focus\\:border-emerald-${shade}:focus{border-color:${primary}!important}`);
  }

  selectors.push(`[data-theme="${theme}"] .shadow-emerald-600\\/25{--tw-shadow-color:${p.shades[600]}40!important}`);
  selectors.push(`[data-theme="${theme}"] .shadow-emerald-600\\/30{--tw-shadow-color:${p.shades[600]}4d!important}`);
  selectors.push(`[data-theme="${theme}"] .text-emerald-50\\/90{color:${p.shades[50]}e6!important}`);
  selectors.push(`[data-theme="${theme}"] .text-emerald-400\\/90{color:${p.shades[400]}e6!important}`);
  selectors.push(`[data-theme="${theme}"] .text-emerald-800\\/90{color:${p.shades[800]}e6!important}`);
  selectors.push(`[data-theme="${theme}"] .border-emerald-600\\/60{border-color:${p.shades[600]}99!important}`);
  selectors.push(`[data-theme="${theme}"] .hover\\:border-emerald-600\\/60:hover{border-color:${p.shades[600]}99!important}`);

  return selectors.join('');
}

/** Apply theme tokens + dynamic Tailwind remaps for site-wide accent color. */
export function applyThemePreferences(theme: ColorTheme): void {
  if (typeof document === 'undefined') return;
  const p = themePalette(theme);
  const root = document.documentElement;
  root.dataset.theme = theme;

  root.style.setProperty('--tg-accent', p.accent);
  root.style.setProperty('--tg-accent-strong', p.accentStrong);
  root.style.setProperty('--theme-primary', p.accent);
  root.style.setProperty('--theme-primary-strong', p.accentStrong);
  root.style.setProperty('--theme-secondary', p.secondary);
  root.style.setProperty('--theme-swatch', p.swatch);

  for (const shade of SHADES) {
    root.style.setProperty(`--theme-${shade}`, p.shades[shade]);
    root.style.setProperty(`--theme-secondary-${shade}`, p.secondaryShades[shade]);
  }

  let styleEl = document.getElementById(THEME_STYLE_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = THEME_STYLE_ID;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = buildOverrideCss(theme);
}
